import { useSetAtom } from 'jotai';
import { useCallback, useEffect, useRef } from 'react';
import { toaster } from '@optiaxiom/globals';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  UpdateUserPreferencesRequest,
  userPreferencesService,
} from '@frontend/api/services/userPreferencesService';
import { FontSize, fontSizeAtom } from '@frontend/atoms/appearanceAtom';
import { type VoicePreferences, voicePreferencesAtom } from '@frontend/atoms/voiceAtom';

import { useDebouncedCallback } from './useDebouncedCallback';

const VOICE_PATCH_DEBOUNCE_MS = 500;

const USER_PREFERENCES_QUERY_KEY = ['opal:user-preferences'];

export function useUserPreferences(userEmail: string | undefined) {
  const setFontSize = useSetAtom(fontSizeAtom);
  const setVoicePreferences = useSetAtom(voicePreferencesAtom);

  return useQuery({
    queryKey: [...USER_PREFERENCES_QUERY_KEY, userEmail],
    queryFn: async () => {
      const prefs = await userPreferencesService.getUserPreferences();
      setFontSize(prefs.font_size);
      setVoicePreferences({
        languageCode: prefs.tts_language_code,
        voice: prefs.tts_voice,
        style: prefs.tts_style,
        speed: Number(prefs.tts_speed),
        wakeWordEnabled: prefs.tts_wake_word_enabled,
      });
      return prefs;
    },
    enabled: !!userEmail,
    gcTime: 1000 * 60 * 60 * 24, // 24 hours
    meta: { persist: true },
  });
}

export function useUpdateUserPreferences(userEmail: string | undefined) {
  const queryClient = useQueryClient();
  const setFontSize = useSetAtom(fontSizeAtom);
  const setVoicePreferences = useSetAtom(voicePreferencesAtom);

  return useMutation({
    mutationFn: (prefs: UpdateUserPreferencesRequest) => {
      return userPreferencesService.updateUserPreferences(prefs);
    },
    onMutate: (prefs: UpdateUserPreferencesRequest) => {
      // Optimistic local update for the fields actually being patched.
      if (prefs.font_size !== undefined) {
        setFontSize(prefs.font_size);
      }
      if (
        prefs.tts_language_code !== undefined ||
        prefs.tts_voice !== undefined ||
        prefs.tts_style !== undefined ||
        prefs.tts_speed !== undefined ||
        prefs.tts_wake_word_enabled !== undefined
      ) {
        setVoicePreferences(prev => ({
          languageCode: prefs.tts_language_code ?? prev.languageCode,
          voice: prefs.tts_voice ?? prev.voice,
          style: prefs.tts_style ?? prev.style,
          speed: prefs.tts_speed ?? prev.speed,
          wakeWordEnabled: prefs.tts_wake_word_enabled ?? prev.wakeWordEnabled,
        }));
      }
    },
    onSuccess: () => {
      toaster.create('Preferences saved', { type: 'success' });
      queryClient.invalidateQueries({
        queryKey: [...USER_PREFERENCES_QUERY_KEY, userEmail],
      });
    },
  });
}

export function useSetFontSizeWithPersist(userEmail: string | undefined) {
  const { mutate } = useUpdateUserPreferences(userEmail);

  return (fontSize: FontSize) => {
    if (userEmail) {
      mutate({ font_size: fontSize });
    }
  };
}

/**
 * Patches voice preferences (any subset) and persists.
 *
 * UI updates immediately via the atom (snappy slider / dropdown
 * feedback). The HTTP PUT is debounced and coalesces accumulated
 * patches into a single request — dragging the speed slider doesn't
 * fire one PUT per pixel, and choosing a voice followed by a style
 * within the debounce window sends both fields together.
 */
export function useSetVoicePreferencesWithPersist(userEmail: string | undefined) {
  const setVoicePreferences = useSetAtom(voicePreferencesAtom);
  const { mutate } = useUpdateUserPreferences(userEmail);

  // Accumulates field-level patches across the debounce window. We flush
  // the merged payload so a fast voice→style→speed sequence becomes one
  // PUT carrying all three fields, instead of three sequential PUTs.
  const pendingRef = useRef<UpdateUserPreferencesRequest>({});

  const flush = useDebouncedCallback(() => {
    const payload = pendingRef.current;
    pendingRef.current = {};
    if (Object.keys(payload).length === 0 || !userEmail) {
      return;
    }
    mutate(payload);
  }, VOICE_PATCH_DEBOUNCE_MS);

  // Flush any in-flight patch on unmount so settings the user changed
  // right before navigating away still get persisted.
  useEffect(() => {
    return () => {
      const payload = pendingRef.current;
      pendingRef.current = {};
      if (Object.keys(payload).length > 0 && userEmail) {
        mutate(payload);
      }
    };
  }, [mutate, userEmail]);

  return useCallback(
    (patch: Partial<VoicePreferences>) => {
      if (!userEmail) {
        return;
      }

      // 1. Update the atom immediately so the UI reflects the change
      //    without waiting for the debounce window.
      setVoicePreferences(prev => ({
        languageCode: patch.languageCode ?? prev.languageCode,
        voice: patch.voice ?? prev.voice,
        style: patch.style ?? prev.style,
        speed: patch.speed ?? prev.speed,
        wakeWordEnabled: patch.wakeWordEnabled ?? prev.wakeWordEnabled,
      }));

      // 2. Stash the wire-format fields into the pending payload.
      if (patch.languageCode !== undefined) {
        pendingRef.current.tts_language_code = patch.languageCode;
      }
      if (patch.voice !== undefined) {
        pendingRef.current.tts_voice = patch.voice;
      }
      if (patch.style !== undefined) {
        pendingRef.current.tts_style = patch.style;
      }
      if (patch.speed !== undefined) {
        pendingRef.current.tts_speed = patch.speed;
      }
      if (patch.wakeWordEnabled !== undefined) {
        pendingRef.current.tts_wake_word_enabled = patch.wakeWordEnabled;
      }

      // 3. Defer the PUT — debounce coalesces a rapid sequence of
      //    changes into one network call.
      flush();
    },
    [userEmail, setVoicePreferences, flush]
  );
}
